import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import CaseReportsTable from "./CaseReportsTable";
import FilterSideBar from "./FilterSideBar";
import FilterTabs from "../../common/components/FilterTabs";
import SearchFilters from "./SearchFilters";
import PillMultiSelect from "../../common/components/PillMultiSelect";

const EMPTY_INITIAL_FILTERS = {
  appointment_slot_group_id: "",
  end_day: "",
  q: "",
  result: "",
  resulted_by_id: "",
  start_day: "",
  selected_tab: "all_case_reports",
  test_configuration_ids: [],
  test_state: "",
};

export const getInitialFilters = (defaultFilters = EMPTY_INITIAL_FILTERS) => {
  const urlParams = new URLSearchParams(window.location.search);
  const filtersFromUrl = {};
  urlParams.forEach((value, key) => {
    filtersFromUrl[key] = value;
  });

  return Object.keys(filtersFromUrl).length > 0 ? filtersFromUrl : defaultFilters;
};

export const updateExportLink = (exportType, linkId, params, testGroup) => {
  const exportLink = document.getElementById(linkId);
  if (!exportLink) return;

  const baseExportUrl = `/organizations/${testGroup.organization_id}/exports`;
  const exportParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    exportParams.set(`export_params[${key}]`, params[key]);
  });
  exportParams.set("export_params[test_group_id]", testGroup.id);
  exportParams.set("export_type", exportType);

  exportLink.href = `${baseExportUrl}?${exportParams.toString()}`;
};

export const updateCustomMessageForm = (filters, testGroupUserIds) => {
  // updates message count on "communications/custom_message_modal"
  const messageCount = document.getElementsByClassName('message-count')[0];
  if (messageCount) messageCount.innerHTML = testGroupUserIds.length;

  // updates selected_test_group_user_ids on "communications/custom_message_modal" => additional protection to sent messages to the correct users; we filter based on params as well
  const selectedTestGroupUserIds = document.getElementById('selected_test_group_user_ids');
  if (selectedTestGroupUserIds) selectedTestGroupUserIds.value = testGroupUserIds;

  // updates hidden params on "communications/custom_message_modal"
  Object.entries(filters).forEach(([key, value]) => {
    let hiddenParam = document.querySelector(`input[name="communication[params][filter_params][${key}]"]`);
    if (hiddenParam) {
      hiddenParam.value = value;
    } else {
      hiddenParam = document.createElement("input");
      hiddenParam.type = "hidden";
      hiddenParam.name = `communication[params][filter_params][${key}]`;
      hiddenParam.value = value;

      const form = document.getElementById("custom_message_form");
      if (form) form.appendChild(hiddenParam);
    }
  });
};

const CaseReportsV2 = ({
  meta_data,
  test_group: testGroup,
  test_group_user_ids_for_displayable_tests,
  tests,
  filter_options,
  filter_tabs,
  flippers,
}) => {

  const [displayableTests, setDisplayableTests] = useState(tests);
  const [metaData, setMetaData] = useState(meta_data);
  const [testGroupUserIdsForDisplayableTests, setTestGroupUserIdsForDisplayableTests] = useState(test_group_user_ids_for_displayable_tests);
  const [filterOptions, setFilterOptions] = useState(filter_options);
  const [filterTabs, setFilterTabs] = useState(filter_tabs);
  const [filters, setFilters] = useState(getInitialFilters());
  const initialForm = useRef(filters);
  const [showStats, setShowStats] = useState(false);
  const [showSideBar, setShowSideBar] = useState(false);

  const metaProps = {
    limit: filters.limit || metaData.per_page,
    nextPage: metaData.next_page,
    currentPage: filters.page || metaData.current_page,
    prevPage: metaData.prev_page,
    setLimit: (limit) => setFieldValue("limit", limit),
    setPage: (page) => setFieldValue("page", page),
    totalPages: metaData.total_pages,
  };

  const handleFetchTests = async () => {
    const response = await axios.get(`/test_groups/${testGroup.slug}/case_reports.json`, { params: filters });

    if (response.status == 200) {
      setDisplayableTests(response.data.tests);
      setFilterOptions(response.data.filter_options);
      setFilterTabs(response.data.filter_tabs);
      setMetaData(response.data.meta_data);
      setTestGroupUserIdsForDisplayableTests(response.data.test_group_user_ids_for_displayable_tests);

      // Update URL without reloading the page
      const url = new URL(window.location);
      Object.keys(filters).forEach((key) => {
        url.searchParams.set(key, filters[key]);
      });
      window.history.pushState({}, "", url);

      // Update export links in erb files dynamically
      updateExportLink("case_reports", "export-cases-link", filters, testGroup);
      updateExportLink("insurance", "export-insurance-link", filters, testGroup);
    } else {
      toastr.error("Something went wrong while filtering the tests");
    }
  };

  const setFieldValue = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleTabClick = (value) => {
    if (value !== filters.selected_tab) {
      setFilters({
        ...filters,
        selected_tab: value,
        page: 1,
      });
    }
  };

  useEffect(() => {
    if (initialForm.current !== filters) {
      handleFetchTests();
    }
  }, [filters]);

  useEffect(() => {
    updateCustomMessageForm(filters, testGroupUserIdsForDisplayableTests);
  }, [displayableTests]);

  return (
    <>
      <FilterTabs
        filters={filters}
        handleTabClick={handleTabClick}
        options={filterTabs}
        selectedTab={filters.selected_tab}
        setFieldValue={setFieldValue}
        setFilters={setFilters}
      />
      <SearchFilters
        filterOptions={filterOptions}
        filters={filters}
        setFieldValue={setFieldValue}
        setFilters={setFilters}
        setShowSideBar={() => setShowSideBar(true)}
        openStats={() => setShowStats(true)}
      />
      <PillMultiSelect
        onClose={() => setShowStats(false)}
        options={filterOptions.service_options}
        selected={filters.test_configuration_ids}
        setSelected={(selectedServices) => {
          setFieldValue("test_configuration_ids", selectedServices)
        }}
        title="SERVICES"
        display={showStats}
      />
      <CaseReportsTable
        tests={displayableTests}
        metaProps={metaProps}
        flippers={flippers}
        handleFetchTests={handleFetchTests}
      />
      <FilterSideBar
        filterOptions={filterOptions}
        filters={filters}
        onClose={() => setShowSideBar(false)}
        setFilters={setFilters}
        showSideBar={showSideBar}
      />
    </>
  );
};

export default CaseReportsV2;
